"use client";

import * as React from "react";
import { CalendarIcon, ChevronDown, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import { useLogFilters } from "@/hooks/use-log-filters";
import { useFilterOptions } from "@/hooks/use-filter-options";
import { useDebounce } from "@/hooks/use-debounce";
import { ERROR_CLASSIFICATIONS, type ErrorClassification } from "@/types/logs";
import { HTTP_METHODS, type ResponseStoredFilter, type FixedStatusFilter } from "@/types/filters";
import { ActiveFilterChips } from "./active-filter-chips";
import { FilterCountSummary } from "./filter-count-summary";
import type { FilterOptionsApiResponse } from "@/types/api";
import {
  combineUtcCalendarDateAndTime,
  formatUtcFilterInstant,
  formatUtcTimeOfDay,
  utcInstantToLocalCalendarDate,
} from "@/utils/date-utils";

interface Option {
  value: string;
  count?: number;
}

/** Keep currently selected values visible even if the related sample omitted them. */
function ensureSelected(options: Option[], selected: string[]): Option[] {
  if (selected.length === 0) return options;
  const seen = new Set(options.map((o) => o.value));
  const missing = selected.filter((value) => !seen.has(value)).map((value) => ({ value }));
  return missing.length > 0 ? [...missing, ...options] : options;
}

function MultiSelectField({
  label,
  placeholder,
  options,
  selected,
  onApply,
  searchable,
  isLoading,
}: {
  label: string;
  placeholder: string;
  options: Option[];
  selected: string[];
  /** Commit draft selections — called only when the user presses Apply. */
  onApply: (next: string[]) => void;
  searchable?: boolean;
  isLoading?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [draft, setDraft] = React.useState<string[]>(selected);
  /** Freeze the option list while the popover is open so parent refetches do not wipe local picks. */
  const [openOptions, setOpenOptions] = React.useState<Option[]>(options);

  const trimmedSearch = searchValue.trim();
  const listOptions = open ? openOptions : options;

  const canUseExactValue =
    Boolean(searchable) &&
    trimmedSearch.length > 0 &&
    !listOptions.some((option) => option.value === trimmedSearch) &&
    !draft.includes(trimmedSearch);

  const draftChanged =
    draft.length !== selected.length || draft.some((value) => !selected.includes(value));

  const toggleDraft = React.useCallback((value: string) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(selected);
      setSearchValue("");
      setOpenOptions(options);
    }
    setOpen(next);
  };

  const handleApply = () => {
    onApply(draft);
    setSearchValue("");
    setOpen(false);
  };

  const handleClearDraft = () => {
    setDraft([]);
    setSearchValue("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between gap-1.5 text-xs" aria-label={label}>
          {label}
          {selected.length > 0 ? (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {selected.length}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Command>
          <CommandInput
            placeholder={placeholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          {/* Only spin when this menu has nothing to show yet — not while typing/picking. */}
          {isLoading && listOptions.length === 0 ? (
            <div
              role="status"
              aria-live="polite"
              aria-busy="true"
              className="flex min-h-[8rem] flex-col items-center justify-center gap-2 bg-popover text-muted-foreground"
            >
              <Loader2 className="size-5 animate-spin" aria-hidden="true" />
              <span className="text-xs">Loading options…</span>
            </div>
          ) : (
            <CommandList>
              <CommandEmpty>
                {searchable && trimmedSearch
                  ? "No matching options. Use “Apply exact value” below, or clear search."
                  : "No options yet. Close and reopen after filters finish loading."}
              </CommandEmpty>
              <CommandGroup>
                {canUseExactValue ? (
                  <CommandItem
                    value={`use-exact:${trimmedSearch}`}
                    onSelect={() => {
                      toggleDraft(trimmedSearch);
                      setSearchValue("");
                    }}
                    className="flex items-center justify-between"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate">Add “{trimmedSearch}”</span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="z-[60] max-w-sm break-all">
                        {trimmedSearch}
                      </TooltipContent>
                    </Tooltip>
                  </CommandItem>
                ) : null}
                {listOptions.map((option) => {
                  const isSelected = draft.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => toggleDraft(option.value)}
                      className="flex items-center justify-between"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          aria-hidden="true"
                          className={`size-3.5 shrink-0 rounded-sm border ${isSelected ? "bg-primary border-primary" : "border-input"}`}
                        />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="min-w-0 flex-1 truncate">{option.value}</span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="z-[60] max-w-sm break-all">
                            {option.value}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
        <div className="flex items-center justify-between gap-2 border-t p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={draft.length === 0}
            onClick={handleClearDraft}
          >
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={!draftChanged}
            onClick={handleApply}
          >
            Apply{draft.length > 0 ? ` (${draft.length})` : ""}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Single-choice filter — same trigger styling/hover as MultiSelectField. */
function SingleSelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const selectedLabel = options.find((option) => option.value === value)?.label ?? label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between gap-1.5 text-xs" aria-label={label}>
          {selectedLabel}
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={`size-3.5 shrink-0 rounded-sm border ${isSelected ? "bg-primary border-primary" : "border-input"}`}
                      />
                      <span className="truncate">{option.label}</span>
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const DEFAULT_FROM_TIME = "00:00:00";
const DEFAULT_TO_TIME = "23:59:59";

function DateRangeField({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from: Date | undefined;
  to: Date | undefined;
  onChange: (from: Date | undefined, to: Date | undefined) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const hasValue = Boolean(from || to);

  const [draftRange, setDraftRange] = React.useState<{ from?: Date; to?: Date }>({});
  const [fromTime, setFromTime] = React.useState(DEFAULT_FROM_TIME);
  const [toTime, setToTime] = React.useState(DEFAULT_TO_TIME);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Sync draft only when opening so edits are not wiped mid-selection.
      setDraftRange({
        from: from ? utcInstantToLocalCalendarDate(from) : undefined,
        to: to ? utcInstantToLocalCalendarDate(to) : undefined,
      });
      setFromTime(from ? formatUtcTimeOfDay(from) : DEFAULT_FROM_TIME);
      setToTime(to ? formatUtcTimeOfDay(to) : DEFAULT_TO_TIME);
    }
    setOpen(next);
  };

  const draftHasDates = Boolean(draftRange.from || draftRange.to);

  const handleApply = () => {
    if (!draftRange.from && !draftRange.to) {
      onChange(undefined, undefined);
      setOpen(false);
      return;
    }
    const startDate = draftRange.from ?? draftRange.to!;
    const endDate = draftRange.to ?? draftRange.from!;
    onChange(
      combineUtcCalendarDateAndTime(startDate, fromTime),
      combineUtcCalendarDateAndTime(endDate, toTime, { endOfSecond: true }),
    );
    setOpen(false);
  };

  const handleClear = () => {
    setDraftRange({});
    setFromTime(DEFAULT_FROM_TIME);
    setToTime(DEFAULT_TO_TIME);
    if (hasValue) {
      onChange(undefined, undefined);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <CalendarIcon className="size-3.5" aria-hidden="true" />
          {hasValue
            ? `${from ? formatUtcFilterInstant(from) : "…"} – ${to ? formatUtcFilterInstant(to) : "…"}`
            : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          <Calendar
            mode="range"
            selected={{ from: draftRange.from, to: draftRange.to }}
            onSelect={(range) => {
              setDraftRange({ from: range?.from, to: range?.to });
            }}
            numberOfMonths={1}
          />
          <div className="flex w-full flex-col justify-center gap-3 border-t p-3 sm:w-40 sm:border-t-0 sm:border-l">
            <div className="space-y-1.5">
              <Label htmlFor={`${label}-from-time`} className="text-xs text-muted-foreground">
                From time
              </Label>
              <Input
                id={`${label}-from-time`}
                type="time"
                step={1}
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value || DEFAULT_FROM_TIME)}
                className="h-8 text-xs"
                disabled={!draftHasDates}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${label}-to-time`} className="text-xs text-muted-foreground">
                To time
              </Label>
              <Input
                id={`${label}-to-time`}
                type="time"
                step={1}
                value={toTime}
                onChange={(e) => setToTime(e.target.value || DEFAULT_TO_TIME)}
                className="h-8 text-xs"
                disabled={!draftHasDates}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 border-t p-2">
          {draftHasDates || hasValue ? (
            <Button variant="ghost" size="sm" className="flex-1 text-xs" onClick={handleClear}>
              Clear dates
            </Button>
          ) : null}
          <Button
            size="sm"
            className="flex-1 text-xs"
            onClick={handleApply}
            disabled={!draftHasDates && !hasValue}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LogFilters({
  onOptionsChange,
  onCountsLoadingChange,
  countOverride,
  lockedRoutes,
  hideRouteFilter = false,
  showResponseTimeFilter = false,
}: {
  onOptionsChange?: (options: FilterOptionsApiResponse | null) => void;
  /** True while matching totals are still being resolved after a filter change. */
  onCountsLoadingChange?: (loading: boolean) => void;
  /** Resolved exact counts from the parent (e.g. full page-1 result). */
  countOverride?: {
    totalCount: number;
    matchingCount: number;
    matchingCountIsPartial: boolean;
  } | null;
  /** Routes that stay applied and cannot be cleared from this UI. */
  lockedRoutes?: string[];
  hideRouteFilter?: boolean;
  showResponseTimeFilter?: boolean;
} = {}) {
  const {
    filters,
    setFilters,
    clearFilters,
    removeFilter,
    toggleUser,
    toggleRoute,
    toggleMethod,
    toggleStatusCode,
    toggleErrorStatus,
    activeCount,
    isPending,
  } = useLogFilters();

  const [searchInput, setSearchInput] = React.useState(filters.search ?? "");
  /** Remount multi-selects after Clear all so draft/search UI resets. */
  const [filterUiResetKey, setFilterUiResetKey] = React.useState(0);
  const debouncedSearch = useDebounce(searchInput, 450);

  const handleClearAll = React.useCallback(() => {
    setSearchInput("");
    setFilterUiResetKey((key) => key + 1);
    if (lockedRoutes?.length) {
      setFilters({
        userIds: undefined,
        routes: lockedRoutes,
        methods: undefined,
        errorStatuses: undefined,
        statusCodes: undefined,
        responseStored: undefined,
        fixedStatus: undefined,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        updatedFrom: undefined,
        updatedTo: undefined,
        responseTimeMinMs: undefined,
        responseTimeMaxMs: undefined,
        search: undefined,
        lookbackAll: undefined,
      });
      return;
    }
    clearFilters();
  }, [clearFilters, filters.createdFrom, filters.createdTo, lockedRoutes, setFilters]);

  // Remount multi-selects when User/Route chips are cleared so local draft/search resets.
  const prevRoutesRef = React.useRef(filters.routes);
  const prevUsersRef = React.useRef(filters.userIds);
  React.useEffect(() => {
    const hadRoutes = (prevRoutesRef.current?.length ?? 0) > 0;
    const hasRoutes = (filters.routes?.length ?? 0) > 0;
    prevRoutesRef.current = filters.routes;
    if (hadRoutes && !hasRoutes) {
      setFilterUiResetKey((key) => key + 1);
    }
  }, [filters.routes]);
  React.useEffect(() => {
    const hadUsers = (prevUsersRef.current?.length ?? 0) > 0;
    const hasUsers = (filters.userIds?.length ?? 0) > 0;
    prevUsersRef.current = filters.userIds;
    if (hadUsers && !hasUsers) {
      setFilterUiResetKey((key) => key + 1);
    }
  }, [filters.userIds]);

  const {
    options,
    totalCount,
    matchingCount,
    matchingCountIsPartial,
    isLoading: isOptionsLoading,
    isCountsLoading,
  } = useFilterOptions(filters);
  const isSearchPending = searchInput.trim() !== (filters.search ?? "");

  React.useEffect(() => {
    onCountsLoadingChange?.(isCountsLoading);
  }, [isCountsLoading, onCountsLoadingChange]);

  React.useEffect(() => {
    if (options === null && totalCount === null) {
      onOptionsChange?.(null);
      return;
    }
    onOptionsChange?.({
      users: options?.users ?? [],
      routes: options?.routes ?? [],
      methods: options?.methods ?? [],
      statusCodes: options?.statusCodes ?? [],
      errorStatuses: options?.errorStatuses ?? [],
      hasMoreUsers: options?.hasMoreUsers ?? false,
      hasMoreRoutes: options?.hasMoreRoutes ?? false,
      totalCount: totalCount ?? 0,
      matchingCount: matchingCount ?? 0,
      matchingCountIsPartial: matchingCount === null ? true : matchingCountIsPartial,
    });
  }, [options, totalCount, matchingCount, matchingCountIsPartial, onOptionsChange]);

  const handleRemoveCreatedRange = React.useCallback(() => {
    setFilters({ createdFrom: undefined, createdTo: undefined, lookbackAll: undefined });
  }, [setFilters]);
  const handleRemoveUpdatedRange = React.useCallback(() => {
    setFilters({ updatedFrom: undefined, updatedTo: undefined });
  }, [setFilters]);
  const handleRemoveSearch = React.useCallback(() => {
    setSearchInput("");
    removeFilter("search");
  }, [removeFilter]);
  const handleRemoveResponseTimeRange = React.useCallback(() => {
    removeFilter("responseTimeMinMs");
  }, [removeFilter]);

  const [responseTimeMinInput, setResponseTimeMinInput] = React.useState(
    filters.responseTimeMinMs !== undefined ? String(filters.responseTimeMinMs) : "",
  );
  const [responseTimeMaxInput, setResponseTimeMaxInput] = React.useState(
    filters.responseTimeMaxMs !== undefined ? String(filters.responseTimeMaxMs) : "",
  );

  React.useEffect(() => {
    setResponseTimeMinInput(
      filters.responseTimeMinMs !== undefined ? String(filters.responseTimeMinMs) : "",
    );
    setResponseTimeMaxInput(
      filters.responseTimeMaxMs !== undefined ? String(filters.responseTimeMaxMs) : "",
    );
  }, [filters.responseTimeMinMs, filters.responseTimeMaxMs]);

  const applyResponseTimeRange = React.useCallback(() => {
    const minRaw = responseTimeMinInput.trim();
    const maxRaw = responseTimeMaxInput.trim();
    const min = minRaw === "" ? undefined : Number.parseInt(minRaw, 10);
    const max = maxRaw === "" ? undefined : Number.parseInt(maxRaw, 10);
    setFilters({
      responseTimeMinMs: Number.isFinite(min) ? min : undefined,
      responseTimeMaxMs: Number.isFinite(max) ? max : undefined,
    });
  }, [responseTimeMinInput, responseTimeMaxInput, setFilters]);

  React.useEffect(() => {
    if ((debouncedSearch || undefined) !== filters.search) {
      setFilters({ search: debouncedSearch || undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const userOptions: Option[] = ensureSelected(options?.users ?? [], filters.userIds ?? []);
  const routeOptions: Option[] = ensureSelected(options?.routes ?? [], filters.routes ?? []);
  // Always keep static method/status options available so dropdowns work even
  // while the sample request is in flight or returns empty.
  const methodOptions: Option[] = ensureSelected(
    options?.methods?.length ? options.methods : HTTP_METHODS.map((m) => ({ value: m })),
    filters.methods ?? [],
  );
  const statusOptions: Option[] = ensureSelected(
    options?.errorStatuses?.length
      ? options.errorStatuses
      : ERROR_CLASSIFICATIONS.map((value) => ({ value })),
    filters.errorStatuses ?? [],
  );
  const statusCodeOptions: Option[] = ensureSelected(
    options?.statusCodes?.length
      ? options.statusCodes
      : [200, 201, 204, 400, 401, 403, 404, 409, 422, 429, 500, 502, 503].map((value) => ({
          value: String(value),
        })),
    (filters.statusCodes ?? []).map(String),
  );

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search id, user, route, message…"
              className="h-8 w-full pr-8 text-xs"
              aria-label="Search logs"
            />
            {isSearchPending || isPending ? (
              <Loader2
                className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            ) : null}
          </div>

          <MultiSelectField
            key={`user-${filterUiResetKey}`}
            label="User"
            placeholder="Filter users…"
            options={userOptions}
            selected={filters.userIds ?? []}
            onApply={(userIds) => setFilters({ userIds: userIds.length > 0 ? userIds : undefined })}
            searchable
            isLoading={isOptionsLoading}
          />
          {!hideRouteFilter ? (
            <MultiSelectField
              key={`route-${filterUiResetKey}`}
              label="Route"
              placeholder="Filter routes…"
              options={routeOptions}
              selected={filters.routes ?? []}
              onApply={(routes) => setFilters({ routes: routes.length > 0 ? routes : undefined })}
              searchable
              isLoading={isOptionsLoading}
            />
          ) : null}
          <MultiSelectField
            key={`method-${filterUiResetKey}`}
            label="Method"
            placeholder="Filter methods…"
            options={methodOptions}
            selected={filters.methods ?? []}
            onApply={(methods) => setFilters({ methods: methods.length > 0 ? methods : undefined })}
            isLoading={isOptionsLoading}
          />
          <MultiSelectField
            key={`status-${filterUiResetKey}`}
            label="Status"
            placeholder="Filter status…"
            options={statusOptions}
            selected={filters.errorStatuses ?? []}
            onApply={(errorStatuses) =>
              setFilters({
                errorStatuses:
                  errorStatuses.length > 0 ? (errorStatuses as ErrorClassification[]) : undefined,
              })
            }
            isLoading={isOptionsLoading}
          />
          <MultiSelectField
            key={`status-code-${filterUiResetKey}`}
            label="Status code"
            placeholder="Filter status codes…"
            options={statusCodeOptions}
            selected={(filters.statusCodes ?? []).map(String)}
            onApply={(codes) =>
              setFilters({
                statusCodes: codes.length > 0 ? codes.map((code) => Number.parseInt(code, 10)) : undefined,
              })
            }
            isLoading={isOptionsLoading}
          />

          <SingleSelectField
            label="Response stored"
            value={filters.responseStored ?? "all"}
            options={[
              { value: "all", label: "All responses" },
              { value: "stored", label: "Stored only" },
              { value: "not-stored", label: "Not stored" },
            ]}
            onChange={(value) =>
              setFilters({ responseStored: value === "all" ? undefined : (value as ResponseStoredFilter) })
            }
          />

          <SingleSelectField
            label="Fixed status"
            value={filters.fixedStatus ?? "all"}
            options={[
              { value: "all", label: "All fixes" },
              { value: "fixed", label: "Fixed only" },
              { value: "not-fixed", label: "Not fixed" },
            ]}
            onChange={(value) =>
              setFilters({ fixedStatus: value === "all" ? undefined : (value as FixedStatusFilter) })
            }
          />

          <DateRangeField
            label="Created"
            from={filters.createdFrom}
            to={filters.createdTo}
            onChange={(from, to) => setFilters({ createdFrom: from, createdTo: to })}
          />
          <DateRangeField
            label="Updated"
            from={filters.updatedFrom}
            to={filters.updatedTo}
            onChange={(from, to) => setFilters({ updatedFrom: from, updatedTo: to })}
          />

          {showResponseTimeFilter ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                  Response time
                  {filters.responseTimeMinMs !== undefined || filters.responseTimeMaxMs !== undefined ? (
                    <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                      1
                    </span>
                  ) : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 space-y-3 p-3" align="start">
                <div className="space-y-1.5">
                  <Label htmlFor="response-time-min" className="text-xs text-muted-foreground">
                    Min (ms)
                  </Label>
                  <Input
                    id="response-time-min"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={responseTimeMinInput}
                    onChange={(e) => setResponseTimeMinInput(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="response-time-max" className="text-xs text-muted-foreground">
                    Max (ms)
                  </Label>
                  <Input
                    id="response-time-max"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={responseTimeMaxInput}
                    onChange={(e) => setResponseTimeMaxInput(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="Any"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => {
                      setResponseTimeMinInput("");
                      setResponseTimeMaxInput("");
                      handleRemoveResponseTimeRange();
                    }}
                  >
                    Clear
                  </Button>
                  <Button size="sm" className="flex-1 text-xs" onClick={applyResponseTimeRange}>
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          ) : null}

          {activeCount > 0 ? (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={handleClearAll}>
              <X className="size-3.5" aria-hidden="true" />
              Clear all ({activeCount})
            </Button>
          ) : null}
        </div>

        <ActiveFilterChips
          filters={filters}
          lockedRoutes={lockedRoutes}
          onRemoveUser={toggleUser}
          onRemoveRoute={toggleRoute}
          onRemoveMethod={toggleMethod}
          onRemoveErrorStatus={toggleErrorStatus}
          onRemoveStatusCode={toggleStatusCode}
          onRemoveResponseStored={() => removeFilter("responseStored")}
          onRemoveFixedStatus={() => removeFilter("fixedStatus")}
          onRemoveCreatedRange={handleRemoveCreatedRange}
          onRemoveUpdatedRange={handleRemoveUpdatedRange}
          onRemoveSearch={handleRemoveSearch}
          onRemoveResponseTimeRange={
            showResponseTimeFilter ? handleRemoveResponseTimeRange : undefined
          }
        />
      </div>

      <FilterCountSummary
        totalCount={countOverride?.totalCount ?? totalCount}
        matchingCount={
          isCountsLoading ? null : (countOverride?.matchingCount ?? matchingCount)
        }
        matchingCountIsPartial={
          isCountsLoading
            ? true
            : countOverride
              ? countOverride.matchingCountIsPartial
              : matchingCountIsPartial
        }
        hasActiveFilters={activeCount > 0}
        isLoading={isCountsLoading}
      />
    </div>
  );
}
